import React, { useEffect, useState } from "react";
import { useError } from "../../../contexts/ErrorContext";
import useApi from "../../../hooks/useGaneshApi";

import { Box, Typography } from "@mui/material";
import EasyAnchorLink from "../../UI/AnchorLink";

import { GridColDef } from "@mui/x-data-grid-premium";
import { renderNumberCell } from "utils/dataGrid";
import GaneshDataGrid from "../../DataGrid/GaneshDataGrid";
import KuebikoQueryCell from "../UI/KuebikoQueryCell";

import "../BrandMetadataReview.css";
import { chain } from "lodash";
import PlacesDrillDownModal from "../UI/PlacesDrillDownModal";

interface NonOmniReviewProps {
  chain_id: string;
  country_code: string;
}

const ReviewNonOmniBizCategories: React.FC<NonOmniReviewProps> = ({
  chain_id,
  country_code,
}) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<any>([]);

  const { showError } = useError();
  const { apiRequest } = useApi();

  const columns_def: GridColDef[] = [
    {
      field: "metadata_category",
      headerName: "Metadata Category",
      description: "The business category from the brand's metadata.",
      type: "string",
      width: 200,
    },
    {
      field: "actual_category",
      headerName: "Non-Omni Places Category",
      description:
        "The business category associated with the brand and country in the places table.",
      type: "string",
      width: 200,
    },
    {
      field: "business_category_count",
      headerName: "Business Category Count",
      description:
        "The count of places from non-omni sources associated with this brand, country, and business category.",
      type: "number",
      width: 150,
      renderCell: renderNumberCell,
    },
    {
      field: "kuebiko_query",
      headerName: "Kuebiko Query",
      description:
        "Copy or view the Kuebiko query used to review places for retraction.",
      filterable: false,
      sortable: false,
      width: 250,
      renderCell: (params) => {
        const queryTemplate = `
        SELECT id, name, country_code, business_category_id, address, chain_id, website,
            external_ids -> 'EXTERNAL_ID' AS "omni_id"
        FROM "places"
        WHERE external_ids -> 'EXTERNAL_ID' IS NULL
        AND country_code = 'COUNTRY_CODE'
        AND business_category_id = 'ACTUAL_CATEGORY'
        `;

        const columnMappings = {
          EXTERNAL_ID: params.row.external_id,
          COUNTRY_CODE: params.row.country_code,
          ACTUAL_CATEGORY: params.row.actual_category,
        };

        return (
          <KuebikoQueryCell
            queryTemplate={queryTemplate}
            columnMappings={columnMappings}
            tooltip="Kuebiko Query"
          />
        );
      },
    },
    {
      field: "drill_down",
      headerName: "View Places",
      width: 150,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const api = "/brand_places/get_brand_places_by_nonomni_biz_category";
        const apiParams = {
          chain_id: chain_id,
          country_code: country_code,
          business_category_id: params.row.actual_category,
        };

        return (
          <>
            <PlacesDrillDownModal api={api} apiParams={apiParams} />
          </>
        );
      },
    },
  ];

  useEffect(() => {
    if (chain_id && country_code) {
      setLoading(true);
      const params = new URLSearchParams({
        chain_id: chain_id,
        country_code: country_code,
      });

      const fetchBusinessCategories = async () => {
        try {
          const response = await apiRequest<any>(
            `/brand_metadata/non_omni_business_category_review?${params.toString()}`,
            {
              method: "GET",
            }
          );
          if (response.data) {
            setData(response.data);
          }
        } catch (error) {
          console.error(error);
          showError(
            `Failed to fetch places business categories to review for chain_id ${chain_id} + ${country_code}.`
          );
        } finally {
          setLoading(false);
        }
      };
      fetchBusinessCategories();
    } else {
      setData([]);
    }
  }, [chain_id, country_code]);

  return (
    <Box className="metadata-review-container">
      <Box display="flex" alignItems="center" id="business-categories">
        <EasyAnchorLink id="business-categories" />
        <Typography variant="h6" gutterBottom sx={{ fontSize: "1.25rem" }}>
          Business Categories
        </Typography>
      </Box>

      <GaneshDataGrid
        gridId="nonOmniReviewBizCategories"
        columns={columns_def}
        data={data}
        loading={loading}
        headerFilters={data.length > 0 ? true : false}
        customHeight={data.length > 0 ? "750px" : "300px"}
      />
    </Box>
  );
};

export default ReviewNonOmniBizCategories;
